import React, { useEffect, useState } from 'react';
import {
  Text, StyleSheet, View, ActivityIndicator,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import DownArrow24 from '../components/24pxIcons/DownArrow24';
import Colors from '../constants/Colors';
import MyGroupsList, { MyUserGroup } from '../components/Profile/MyGroupsList';
import ProfileInformationIcon from '../components/16pxIcons/ProfileInformationIcon';
import Style from '../constants/Style';
import PasswordIcon from '../components/24pxIcons/PasswordIcon';
import MyGroupsIcon from '../components/24pxIcons/MyGroupsIcon';
import getMyUserGroups from '../requests/Users/GetMyUserGroups';
import PersonalInformation from '../components/Profile/PersonalInformation';
import ChangePassword from '../components/Profile/ChangePassword';
import isMobile from '../components/IsMobile';
import ProfileSectionHeader from '../components/Profile/ProfileSectionHeader';
import AlertModal from '../components/AlertModal';
import LogoutIcon from '../components/LogoutIcon';
import NotificationPreferences from '../components/Profile/NotificationPreferences';
import getMyUser from '../requests/Users/GetMyUser';
import NotificationIcon from '../components/24pxIcons/NotificationIcon';
import signOut from '../components/Signout';
import { logEvent } from '../components/LogEvent';
import { HappyScoreCategory } from '../constants/Types';
import PressableStyled from '../components/PressableStyled';
import { updateUsername } from '../redux/userSlice';

// eslint-disable-next-line no-shadow, no-unused-vars
enum Section {
  // eslint-disable-next-line no-unused-vars
  PERSONAL_INFORMATION = 'Personal information', CHANGE_PASSWORD = 'Password',
  // eslint-disable-next-line no-unused-vars
  MY_GROUPS = 'My groups', NOTIFICATION = 'Notifications',
}

export default function ProfileScreen() {
  const dispatch = useDispatch();
  const isAdmin: boolean = useSelector((state:
    { user: { isAdmin: boolean } }) => state.user.isAdmin);
  const [selectedSection, setSelectedSection] = useState<Section>(isMobile ? null
    : Section.PERSONAL_INFORMATION);
  const [changePasswordEnabled, setChangePasswordEnabled] = useState<boolean>(false);
  const [client, setClient] = useState<string>('Unknown');
  const [apiDomain, setApiDomain] = useState<string>('api.hereworks.tech');
  const [email, setEmail] = useState<string>('');
  const [myUserGroups, setMyUserGroups] = useState<MyUserGroup[]>(null);
  const [alertVisible, setAlertVisible] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [energyEnabled, setEnergyEnabled] = useState<boolean>(true);
  const [internalEnabled, setInternalEnabled] = useState<boolean>(true);
  const [externalEnabled, setExternalEnabled] = useState<boolean>(true);
  const [occupancyEnabled, setOccupancyEnabled] = useState<boolean>(true);
  const [amenitiesEnabled, setAmenitiesEnabled] = useState<boolean>(true);
  const [itEnabled, setItEnabled] = useState<boolean>(true);
  const [ergonomicsEnabled, setErgonomicsEnabled] = useState<boolean>(true);
  const [satisfactionEnabled, setSatisfactionEnabled] = useState<boolean>(true);
  const [happyEnabled, setHappyEnabled] = useState<boolean>(true);
  const [preferencesLoaded, setPreferencesLoaded] = useState<boolean>(null);
  const [initalLoad, setInitialLoad] = useState<boolean>(true);
  const [solveClient, setSolveClient] = useState<boolean>(true);
  const navigation = useNavigation();
  const marginTop: number = useSafeAreaInsets().top;

  useEffect(() => {
    AsyncStorage.getItem('changePasswordEnabled').then((value: string) => {
      setChangePasswordEnabled(value === 'true');
    });
    AsyncStorage.getItem('clientName').then((name) => {
      if (name) setClient(name);
    });
    AsyncStorage.getItem('email').then(setEmail);
    AsyncStorage.getItem('apiDomain').then((currentApi) => {
      setApiDomain(currentApi ?? 'api.hereworks.tech');
    });
    AsyncStorage.getItem('happyEnabled').then((value) => {
      setSolveClient(value !== 'enabled');
    });
  }, []);

  const loadData = () => {
    getMyUser().then((result) => {
      if (!result) {
        setPreferencesLoaded(false);
        return;
      }
      const userFirstName: string | undefined = result.firstName?.trim();
      const userLastName: string | undefined = result.lastName?.trim();
      if (userFirstName) setFirstName(userFirstName);
      if (userLastName) setLastName(userLastName);
      if (userFirstName && userLastName) {
        dispatch(updateUsername({ firstName: userFirstName, lastName: userLastName }));
      } else setSelectedSection(Section.PERSONAL_INFORMATION);
      setEnergyEnabled(result.notificationPreferences
        ?.enabledCategories[HappyScoreCategory.ENERGY]);
      setInternalEnabled(result.notificationPreferences
        ?.enabledCategories[HappyScoreCategory.INTERNAL_ENVIRONMENT]);
      setExternalEnabled(result.notificationPreferences
        ?.enabledCategories[HappyScoreCategory.EXTERNAL_ENVIRONMENT]);
      setOccupancyEnabled(result.notificationPreferences
        ?.enabledCategories[HappyScoreCategory.OCCUPANCY]);
      setAmenitiesEnabled(result.notificationPreferences
        ?.enabledCategories[HappyScoreCategory.AMENITIES]);
      setItEnabled(result.notificationPreferences
        ?.enabledCategories[HappyScoreCategory.IT]);
      setErgonomicsEnabled(result.notificationPreferences
        ?.enabledCategories[HappyScoreCategory.ERGONOMICS]);
      setSatisfactionEnabled(result.notificationPreferences
        ?.enabledCategories[HappyScoreCategory.SATISFACTION]);
      setHappyEnabled(result.notificationPreferences
        ?.enabledCategories[HappyScoreCategory.HAPPY_SCORE]);
      setPreferencesLoaded(true);
      setInitialLoad(false);
    }).catch(() => setPreferencesLoaded(false));
    if (!(email?.length > 0 && client?.length > 0)) return;
    getMyUserGroups(`${client}-${email}`).then((res) => {
      setMyUserGroups(res?.GroupsDetails ?? []);
    }).catch(() => setMyUserGroups([]));
  };

  useEffect(() => {
    loadData();
    const unsubscribe = navigation.addListener('focus', loadData);
    return unsubscribe;
  }, [email, client]);

  const renderIcon = (section: Section) => {
    const selected: boolean = selectedSection === section;
    switch (section) {
      case Section.PERSONAL_INFORMATION:
        return <ProfileInformationIcon color={selected ? '#FFF' : Colors.light.hereworksBlue500} />;
      case Section.CHANGE_PASSWORD:
        return <PasswordIcon color={selected ? '#FFF' : Colors.light.hereworksBlue500} />;
      case Section.MY_GROUPS:
        return <MyGroupsIcon color={selected ? '#FFF' : Colors.light.hereworksBlue500} />;
      case Section.NOTIFICATION:
        return <NotificationIcon color={selected ? '#FFF' : Colors.light.hereworksBlue500} />;
      default: return null;
    }
  };

  const renderSidebarButton = (section: Section, testID: string) => {
    const selected: boolean = selectedSection === section;
    return <PressableStyled
      style={[Style.row, styles.button, selected && styles.buttonSelected]}
      onPress={() => {
        logEvent('Profile', 'Sidebar_item');
        setSelectedSection(section);
      }}
      {...{ testID }}
    >
      <View style={Style.row}>
        <View style={styles.sectionIcon}>{renderIcon(section)}</View>
        <Text style={[styles.itemTitle, selected && styles.selectedText]}>
          {section}
        </Text>
      </View>
      <DownArrow24
        color={selected ? '#fff' : Colors.light.hereworksBlue500}
        style={styles.arrowIcon}
      />
    </PressableStyled>;
  };

  const renderMain = () => {
    if (initalLoad) {
      return <ActivityIndicator style={{ alignSelf: 'flex-start', margin: 24 }} size={'large'} />;
    }
    switch (selectedSection) {
      case Section.PERSONAL_INFORMATION: return <PersonalInformation {...{
        email, firstName, lastName, isAdmin, setFirstName, setLastName,
      }} />;
      case Section.CHANGE_PASSWORD: return <ChangePassword />;
      case Section.MY_GROUPS: return <MyGroupsList
        {...{
          myUserGroups, setMyUserGroups, email, client,
        }}
      />;
      case Section.NOTIFICATION: {
        if (solveClient) return null;
        return <NotificationPreferences {...{
          energyEnabled,
          internalEnabled,
          externalEnabled,
          occupancyEnabled,
          amenitiesEnabled,
          itEnabled,
          ergonomicsEnabled,
          satisfactionEnabled,
          happyEnabled,
          setEnergyEnabled,
          setInternalEnabled,
          setExternalEnabled,
          setOccupancyEnabled,
          setAmenitiesEnabled,
          setItEnabled,
          setErgonomicsEnabled,
          setSatisfactionEnabled,
          setHappyEnabled,
          preferencesLoaded,
        }} />;
      }
      default: return null;
    }
  };

  const logout = () => {
    logEvent('Profile', 'Logout_pressed');
    setAlertVisible(true);
  };

  return <View style={styles.container}>
    {!isMobile && <View style={styles.appInfoSection}>
      <Text style={styles.appInfo}>{`Client: ${client}\nAPI: ${apiDomain}`}</Text>
      <PressableStyled
        style={styles.logoutButton}
        onPress={signOut}
      >
        <Text style={styles.logoutText}>{'Logout'}</Text>
      </PressableStyled>
    </View>}
    <View style={[Style.row, styles.titleContainer, isMobile && { marginTop }]}>
      <Text style={styles.title}>{'My profile'}</Text>
    </View>
    <View style={styles.contentContainer}>
      {(!isMobile || !selectedSection) && <View style={styles.sideBar}>
        {!isMobile && <View style={styles.spacing} />}
        {renderSidebarButton(Section.PERSONAL_INFORMATION, 'personal-info-btn')}
        {changePasswordEnabled && renderSidebarButton(Section.CHANGE_PASSWORD, 'password-btn')}
        {renderSidebarButton(Section.MY_GROUPS, 'my-groups-btn')}
        {solveClient ? <></> : renderSidebarButton(Section.NOTIFICATION, 'notifications-btn')}
        {isMobile && <PressableStyled
          style={[Style.row, styles.button]}
          onPress={logout}
        >
          <View style={Style.row}>
            <View style={styles.sectionIcon}>
              <LogoutIcon color={Colors.light.hereworksBlue500} />
            </View>
            <Text style={styles.itemTitle}>{'Logout'}</Text>
          </View>
          <DownArrow24
            color={Colors.light.hereworksBlue500}
            style={styles.arrowIcon}
          />
        </PressableStyled>}
      </View>}
      {selectedSection && <View style={styles.mainContent}>
        <ProfileSectionHeader
          title={selectedSection}
          back={() => setSelectedSection(null)}
        />
        {renderMain()}
      </View>}
    </View>
    <AlertModal
      modalVisible={alertVisible}
      title={'Sign out'}
      message={'Are you sure you want to sign out?'}
      action={() => {
        setLoading(true);
        signOut();
      }}
      actionTitle='OK'
      setModalVisible={setAlertVisible}
    />
    <ActivityIndicator
      size='large'
      style={Style.activityIndicator}
      animating={loading}
    />
  </View>;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    paddingTop: 32,
    paddingBottom: isMobile ? 0 : 32,
    paddingHorizontal: isMobile ? 0 : 40,
    backgroundColor: Colors.light.hereworksBackground,
  },
  contentContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    backgroundColor: isMobile ? 'transparent' : '#FFF',
    paddingStart: isMobile ? 0 : 8,
    borderRadius: 24,
  },
  mainContent: {
    flex: 1,
    paddingTop: isMobile ? 0 : 24,
    paddingBottom: isMobile ? 10 : 24,
    alignSelf: 'stretch',
  },
  titleContainer: {
    marginBottom: isMobile ? 32 : 24,
    marginHorizontal: isMobile ? 24 : undefined,
  },
  title: {
    fontSize: isMobile ? 22 : 32,
    lineHeight: isMobile ? 33 : 48,
    fontFamily: isMobile ? 'Poppins_600SemiBold' : 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
  },
  button: {
    borderRadius: 8,
    paddingHorizontal: isMobile ? 24 : 12,
    paddingVertical: 11,
  },
  buttonSelected: {
    backgroundColor: Colors.light.hereworksBlue500,
  },
  selectedText: {
    color: '#fff',
  },
  itemTitle: {
    fontFamily: 'Poppins_400Regular',
    fontSize: 14,
    lineHeight: 22,
    color: Colors.light.hereworksBlack500,
    marginStart: 8,
  },
  appInfo: {
    color: Colors.light.hereworksLightBlue800,
    fontFamily: 'Poppins_400Regular',
    fontSize: 10,
    marginRight: 8,
  },
  appInfoSection: {
    position: 'absolute',
    flexDirection: 'row',
    right: 40,
    top: 24,
  },
  sideBar: {
    width: isMobile ? '100%' : 260,
    paddingEnd: isMobile ? 0 : 14,
    borderEndColor: Colors.light.hereworksBlue100,
    borderEndWidth: isMobile ? 0 : 1,
    alignSelf: 'stretch',
  },
  spacing: {
    height: 20,
  },
  logoutButton: {
    borderColor: Colors.light.hereworksBlue400,
    borderWidth: 1,
    borderRadius: 8,
    padding: 8,
  },
  logoutText: {
    fontFamily: 'Poppins_500Medium',
    fontSize: 16,
    color: Colors.light.hereworksBlue500,
  },
  arrowIcon: {
    transform: [{ rotateZ: '270deg' }],
    position: 'absolute',
    right: 18,
  },
  sectionIcon: {
    width: 24,
    alignItems: 'center',
  },
});
